import { useEffect, useMemo, useState } from "react";
import type { PlatformClient } from "../../platform/supabase";

type CorrelationRow={
  correlationId:string;
  lastOccurredAt:string;
  traceCount:number;
  auditCount:number;
  eventCount:number;
  lastTraceType:string;
  lastResourceType?:string|null;
  lastResourceId?:string|null;
};

type LearnerRow={
  learnerId:string;
  displayName:string;
  schoolName?:string|null;
  gradeLevel?:string|null;
  classLabel?:string|null;
  lastActivityAt?:string|null;
};

type TraceItem={
  occurredAt:string;
  correlationId?:string;
  kind:string;
  type:string;
  resourceType?:string|null;
  resourceId?:string|null;
  status:string;
  actorName?:string|null;
  organizationName?:string|null;
  producer?:string|null;
  eventId?:string|null;
};

function formatDate(value?:string|null){
  if(!value) return "-";
  return new Date(value).toLocaleString("ko-KR",{
    year:"numeric",month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit",second:"2-digit"
  });
}

export function AdminAuditTrace({
  client,
  initialCorrelationId
}:{client:PlatformClient;initialCorrelationId?:string}){
  const [mode,setMode]=useState<"CORRELATION"|"LEARNER">("CORRELATION");
  const [correlations,setCorrelations]=useState<CorrelationRow[]>([]);
  const [learners,setLearners]=useState<LearnerRow[]>([]);
  const [selectedCorrelation,setSelectedCorrelation]=useState("");
  const [selectedLearner,setSelectedLearner]=useState("");
  const [trace,setTrace]=useState<TraceItem[]>([]);
  const [title,setTitle]=useState("");
  const [query,setQuery]=useState("");
  const [error,setError]=useState("");

  useEffect(()=>{
    let active=true;
    Promise.all([
      client.rpc("admin_audit_index",{p_limit:100}),
      client.rpc("admin_lifecycle_learners",{p_query:null,p_limit:100})
    ]).then(([audit,learner])=>{
      if(!active) return;
      if(audit.error) setError(audit.error.message);
      else{
        const rows=(audit.data || []) as CorrelationRow[];
        setCorrelations(rows);
        setSelectedCorrelation((current)=>
          initialCorrelationId && rows.some((item)=>item.correlationId===initialCorrelationId)
            ? initialCorrelationId
            : rows.some((item)=>item.correlationId===current)
              ? current
              : rows[0]?.correlationId || ""
        );
      }
      if(learner.error) setError(learner.error.message);
      else{
        const rows=(learner.data || []) as LearnerRow[];
        setLearners(rows);
        setSelectedLearner(rows[0]?.learnerId || "");
      }
    });
    return()=>{active=false;};
  },[client,initialCorrelationId]);

  useEffect(()=>{
    if(!initialCorrelationId)return;
    setMode("CORRELATION");
    setSelectedCorrelation(initialCorrelationId);
  },[initialCorrelationId]);

  useEffect(()=>{
    if(mode!=="CORRELATION" || !selectedCorrelation) return;
    let active=true;
    client.rpc("admin_correlation_trace",{p_correlation_id:selectedCorrelation}).then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else{
        const result=data as {correlationId:string;items:TraceItem[]};
        setTitle(result.correlationId);
        setTrace(result.items || []);
      }
    });
    return()=>{active=false;};
  },[client,mode,selectedCorrelation]);

  useEffect(()=>{
    if(mode!=="LEARNER" || !selectedLearner) return;
    let active=true;
    client.rpc("admin_learner_lifecycle_trace",{p_learner_id:selectedLearner}).then(({data,error})=>{
      if(!active) return;
      if(error) setError(error.message);
      else{
        const result=data as {displayName:string;items:TraceItem[]};
        setTitle(result.displayName);
        setTrace(result.items || []);
      }
    });
    return()=>{active=false;};
  },[client,mode,selectedLearner]);

  const filteredLearners=useMemo(()=>{
    const value=query.trim().toLowerCase();
    if(!value) return learners;
    return learners.filter((item)=>
      item.displayName.toLowerCase().includes(value)
      || (item.schoolName || "").toLowerCase().includes(value)
    );
  },[learners,query]);

  return (
    <section className="admin-audit-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Audit / Event Trace</p>
          <h2>감사·이력</h2>
          <p className="route-description">한 작업의 Correlation과 한 학생의 전체 Golden Path를 서로 다른 관점으로 추적합니다.</p>
        </div>
        <span className="phase-badge">BUILD-06</span>
      </div>

      <div className="audit-mode-tabs">
        <button className={mode==="CORRELATION" ? "filter-chip active" : "filter-chip"} onClick={()=>setMode("CORRELATION")}>작업별 Correlation</button>
        <button className={mode==="LEARNER" ? "filter-chip active" : "filter-chip"} onClick={()=>setMode("LEARNER")}>학생 Lifecycle</button>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="audit-workspace">
        <aside className="audit-list">
          {mode==="CORRELATION" ? (
            <>
              <div className="panel-title"><strong>최근 작업</strong><span>{correlations.length}건</span></div>
              {correlations.map((item)=>(
                <button key={item.correlationId} className={selectedCorrelation===item.correlationId ? "audit-list-row active" : "audit-list-row"}
                  onClick={()=>setSelectedCorrelation(item.correlationId)}>
                  <strong>{item.lastTraceType}</strong>
                  <span>{item.traceCount} trace · Audit {item.auditCount} / Event {item.eventCount}</span>
                  <small>{formatDate(item.lastOccurredAt)}</small>
                </button>
              ))}
            </>
          ):(
            <>
              <div className="audit-search"><input value={query} onChange={(event)=>setQuery(event.target.value)} placeholder="학생 또는 학교 검색" /></div>
              <div className="panel-title"><strong>학생</strong><span>{filteredLearners.length}명</span></div>
              {filteredLearners.map((item)=>(
                <button key={item.learnerId} className={selectedLearner===item.learnerId ? "audit-list-row active" : "audit-list-row"}
                  onClick={()=>setSelectedLearner(item.learnerId)}>
                  <strong>{item.displayName}</strong>
                  <span>{item.schoolName || "학교 미확인"}{item.gradeLevel ? ` · ${item.gradeLevel}학년` : ""}</span>
                  <small>{formatDate(item.lastActivityAt)}</small>
                </button>
              ))}
            </>
          )}
        </aside>

        <div className="audit-detail">
          <div className="audit-detail-head">
            <div>
              <p className="eyebrow">{mode==="CORRELATION" ? "Correlation Trace" : "Learner Golden Path"}</p>
              <h2>{title || "추적 대상을 선택하세요"}</h2>
            </div>
            <span className="phase-badge">{trace.length} trace</span>
          </div>

          <div className="audit-timeline">
            {trace.length ? trace.map((item,index)=>(
              <article className="audit-trace-row" key={`${item.occurredAt}-${item.type}-${index}`}>
                <div className={item.kind==="EVENT" ? "trace-dot event" : "trace-dot audit"} />
                <div className="audit-trace-body">
                  <div className="audit-trace-top">
                    <strong>{item.type}</strong>
                    <span className="status-pill">{item.kind}</span>
                  </div>
                  <p>{item.resourceType || "Resource"}{item.resourceId ? ` · ${item.resourceId}` : ""}</p>
                  <small>
                    {formatDate(item.occurredAt)}
                    {item.actorName ? ` · ${item.actorName}` : ""}
                    {item.organizationName ? ` · ${item.organizationName}` : ""}
                    {item.producer ? ` · ${item.producer}` : ""}
                  </small>
                  {item.correlationId && mode==="LEARNER" && <code>{item.correlationId}</code>}
                </div>
              </article>
            )):<p className="case-empty">표시할 Audit/Event trace가 없습니다.</p>}
          </div>
        </div>
      </div>
    </section>
  );
}
